import * as React from "react";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { Form } from "informedv4";
import { UserForm, UserFormState } from "./UserForm";
import { useParams } from "react-router-dom";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { GlobalAuthorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { ApiLanguages } from "../../misc/api/AccountServer/AccountApi";
import { MapToSiteOptions, MapToRoleOptions } from "../../misc/OptionFormatters";

export const EditUserScreen = () => {
    const { id } = useParams<{ id: string }>();
    const client = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext();
    const { countries } = useReferenceContextProvider();
    const [languageOptions, setLanguageOptions] = React.useState([]);
    const [timeZoneOptions, setTimeZoneOptions] = React.useState([]);
    const [initialValues, setInitialValues] = React.useState<UserFormState>();
    const title = "Edit User";

    React.useEffect(() => {      
        const getSitesOptions = async (sitesIds: string[]) => {
            if (!sitesIds || sitesIds.length === 0)
                return [];

            const sites = await client.client.getSites("id,number", undefined, undefined, "study($select=name)");
            return MapToSiteOptions(sites.value.filter(e => sitesIds.some(x => e.id == x)));
        }

        const getRolesOptions = async (rolesIds: string[]) => {
            const roleResult = await client.client.getRoles("id,name", undefined, undefined, "study($select=id,name)");
            const userRoles = roleResult.value.filter(r => rolesIds.includes(r.id));
            return MapToRoleOptions(userRoles);
        }

        const fetchLanguages = async () => {
            const languages = await ApiLanguages();
            setLanguageOptions(languages.data);
            return languages.data;
        }

        const fetchTimeZoneOptions = async () => {
            const timeZones = await client.client.getTimeZones("id,name", undefined, "sortOrder");
            setTimeZoneOptions(timeZones.value);
            return timeZones.value;
        }

        const fillUserForm = async () => {
            if (id) {
                const user = await client.client.getUser(id);

                const languages = await fetchLanguages();
                const timeZones = await fetchTimeZoneOptions();

                const formState: UserFormState = {
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    roles: await getRolesOptions(user.roleIds),
                    sites: await getSitesOptions(user.siteIds),
                    language: languages.find(l => l.code === user.preferredLanguageCode),
                    country: countries.find(c => c.id === user.countryId),
                    timeZone: timeZones.find(t => t.id === user.timeZoneId),
                    spirotracUserGroup: user.spirotracUserGroupId,
                    registeredForMobileApp: user.isRegisteredForMobileApp,
                    isDisabled: user.isDisabled,
                };
                setInitialValues(formState);
            }
        }

        fillUserForm();

        return () => {
            client.abortController.abort();
        }
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading User Data..." />;
    }
    
    return (
        <DocumentTitle title={`${title} | Vitalograph Web Portal`}>
            <GlobalAuthorization permissionId={PermissionEnum.EditUsers} currentPermissions={currentUserPermissions}>
                <Form validateOn="submit" initialValues={initialValues as unknown as Record<string, unknown>} noValidate>
                    <UserForm header={title} userId={id} languageOptions={languageOptions} timeZoneOptions={timeZoneOptions} />
                </Form>
            </GlobalAuthorization>
        </DocumentTitle>
    );
}
